import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { subscriptionApi } from './api';

const RAZORPAY_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

/**
 * Open the Prime sheet from anywhere. Follows the same window-event pattern the
 * chat widget already uses, so no page needs to thread the modal down as a prop.
 *
 * @param {string} [reason] one line explaining what the user was trying to do
 */
export const openPrimeModal = (reason) => {
    window.dispatchEvent(new CustomEvent('openPrime', { detail: { reason } }));
};

/**
 * Load Razorpay's checkout script once and reuse it.
 * Resolves false if the script can't load (offline, blocker) so the caller can
 * show a real message instead of a dead button.
 */
const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);

    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
        existing.addEventListener('load', () => resolve(!!window.Razorpay));
        existing.addEventListener('error', () => resolve(false));
        return;
    }

    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT;
    script.async = true;
    script.onload = () => resolve(!!window.Razorpay);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
});

const IconLock = ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

const IconCheck = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const BENEFITS = [
    'See owner contact numbers instantly',
    'Unlimited chat with owners and flatmates',
    'Call and WhatsApp directly — no waiting',
    'Valid for a full month, no auto-renewal',
];

/**
 * The paywall sheet. Opened whenever an action needs Prime — a locked contact
 * card, a chat button, or a 402 coming back from the API.
 *
 * Payment flow: ask our server for an order (it decides the amount), hand the
 * order to Razorpay checkout, then post the signed result back to our server for
 * verification. The membership is only granted by that server-side check — the
 * browser never decides that a payment succeeded.
 */
const PrimeModal = ({ open, onClose, reason, onSuccess }) => {
    const { tokens, user, subscription, refreshSubscription } = useAuth();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (open) {
            setError('');
            setDone(false);
            // Pre-warm the script so the first tap on "Pay" feels instant.
            loadRazorpay();
        }
    }, [open]);

    const priceInr = subscription?.priceInr ?? 99;
    const durationDays = subscription?.durationDays ?? 30;

    const handlePay = useCallback(async () => {
        if (!tokens?.access) {
            setError('Please sign in first.');
            return;
        }
        setBusy(true);
        setError('');

        try {
            const ready = await loadRazorpay();
            if (!ready) {
                throw new Error('Could not load the payment window. Check your connection or ad blocker.');
            }

            const order = await subscriptionApi.createOrder(tokens.access);

            const rzp = new window.Razorpay({
                key: order.razorpayKeyId,
                amount: order.amountPaise,
                currency: order.currency,
                order_id: order.orderId,
                name: 'FlatLoop',
                description: `${order.planName} · ${order.durationDays} days`,
                // Merchant logo on the checkout. Absolute, not relative: Razorpay
                // renders this inside their own iframe, so a bare "/prime-logo.png"
                // would resolve against their origin and 404.
                image: `${window.location.origin}/prime-logo.png`,
                prefill: {
                    name: order.customerName || user?.name || '',
                    contact: order.contactPhone || '',
                },
                theme: { color: '#2563EB' },
                modal: {
                    ondismiss: () => setBusy(false),
                },
                handler: async (response) => {
                    // Never trust this callback on its own — it is client-side.
                    // The server re-checks the signature against Razorpay before
                    // it grants anything.
                    try {
                        await subscriptionApi.verify({
                            razorpayOrderId: response.razorpay_order_id,
                            razorpayPaymentId: response.razorpay_payment_id,
                            razorpaySignature: response.razorpay_signature,
                        }, tokens.access);

                        await refreshSubscription();
                        setDone(true);
                        setBusy(false);
                        if (onSuccess) onSuccess();
                    } catch (e) {
                        setBusy(false);
                        setError(
                            e.message ||
                            'Payment went through but we could not confirm it. It will be applied shortly — please refresh in a minute.'
                        );
                        // The webhook is the safety net here, so re-check status.
                        refreshSubscription();
                    }
                },
            });

            rzp.on('payment.failed', (resp) => {
                setBusy(false);
                setError(resp?.error?.description || 'Payment failed. You have not been charged.');
            });

            rzp.open();
        } catch (e) {
            setBusy(false);
            setError(e.message || 'Could not start the payment. Please try again.');
        }
    }, [tokens, user, refreshSubscription, onSuccess]);

    if (!open) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="FlatLoop Prime"
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,0.6)',
                backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', padding: '20px',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: 'white', borderRadius: '28px', maxWidth: '440px', width: '100%',
                    overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.3)', maxHeight: '92vh',
                    overflowY: 'auto',
                }}
            >
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(140deg, #1E40AF 0%, #2563EB 60%, #3B82F6 100%)',
                    padding: '32px 28px 28px', color: 'white', position: 'relative',
                }}>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.18)',
                            border: 'none', color: 'white', width: '34px', height: '34px', borderRadius: '50%',
                            cursor: 'pointer', fontSize: '18px', lineHeight: 1,
                        }}
                    >×</button>

                    <div style={{
                        width: '52px', height: '52px', borderRadius: '16px', background: 'rgba(255,255,255,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px',
                    }}>
                        <IconLock size={26} />
                    </div>

                    <h2 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 800 }}>
                        {done ? 'You’re Prime 🎉' : 'Unlock with FlatLoop Prime'}
                    </h2>
                    <p style={{ margin: 0, fontSize: '15px', opacity: 0.9, lineHeight: 1.5 }}>
                        {done
                            ? 'Contact numbers and chat are now open for you.'
                            : reason || 'Contact numbers and chat are for Prime members.'}
                    </p>
                </div>

                <div style={{ padding: '28px' }}>
                    {done ? (
                        <button
                            onClick={onClose}
                            style={{
                                width: '100%', padding: '16px', border: 'none', borderRadius: '16px',
                                background: 'linear-gradient(135deg, #1E40AF, #2563EB)', color: 'white',
                                fontWeight: 800, fontSize: '16px', cursor: 'pointer',
                            }}
                        >Continue</button>
                    ) : (
                        <>
                            {/* Price */}
                            <div style={{
                                display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '24px',
                                justifyContent: 'center',
                            }}>
                                <span style={{ fontSize: '44px', fontWeight: 800, color: '#111827', lineHeight: 1 }}>
                                    ₹{priceInr}
                                </span>
                                <span style={{ fontSize: '16px', color: '#6B7280', fontWeight: 600 }}>
                                    / {durationDays} days
                                </span>
                            </div>

                            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px' }}>
                                {BENEFITS.map(item => (
                                    <li key={item} style={{
                                        display: 'flex', alignItems: 'flex-start', gap: '12px',
                                        padding: '10px 0', color: '#1F2937', fontSize: '15px', fontWeight: 600,
                                    }}>
                                        <span style={{ marginTop: '2px', flexShrink: 0 }}><IconCheck /></span>
                                        {item}
                                    </li>
                                ))}
                            </ul>

                            {error && (
                                <div role="alert" style={{
                                    background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C',
                                    padding: '12px 16px', borderRadius: '14px', fontSize: '14px',
                                    marginBottom: '16px', fontWeight: 600, lineHeight: 1.5,
                                }}>{error}</div>
                            )}

                            <button
                                onClick={handlePay}
                                disabled={busy || subscription?.purchaseEnabled === false}
                                style={{
                                    width: '100%', padding: '16px', border: 'none', borderRadius: '16px',
                                    background: busy ? '#94A3B8' : 'linear-gradient(135deg, #1E40AF, #2563EB)',
                                    color: 'white', fontWeight: 800, fontSize: '16px',
                                    cursor: busy ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 10px 28px rgba(37,99,235,0.32)',
                                }}
                            >
                                {busy ? 'Opening payment…' : `Pay ₹${priceInr} securely`}
                            </button>

                            {subscription?.purchaseEnabled === false && (
                                <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#B91C1C', textAlign: 'center' }}>
                                    Payments are temporarily unavailable. Please try again later.
                                </p>
                            )}

                            <p style={{
                                margin: '16px 0 0', fontSize: '12px', color: '#9CA3AF', textAlign: 'center',
                                lineHeight: 1.6,
                            }}>
                                Secure payment by Razorpay · One-time charge, no auto-renewal
                                <br />
                                {/* Reachable at the point of purchase, which is what the
                                    e-commerce rules and Razorpay both expect. */}
                                <a href="/pricing" style={{ color: '#6B7280', textDecoration: 'underline' }}>Pricing</a>
                                {' · '}
                                <a href="/refund-policy" style={{ color: '#6B7280', textDecoration: 'underline' }}>Refund Policy</a>
                                {' · '}
                                <a href="/terms" style={{ color: '#6B7280', textDecoration: 'underline' }}>Terms</a>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Mounted once at the app root; listens for {@link openPrimeModal}.
 */
export const PrimeModalHost = () => {
    const [state, setState] = useState({ open: false, reason: '' });

    useEffect(() => {
        const handler = (e) => setState({ open: true, reason: e.detail?.reason || '' });
        window.addEventListener('openPrime', handler);
        return () => window.removeEventListener('openPrime', handler);
    }, []);

    return (
        <PrimeModal
            open={state.open}
            reason={state.reason}
            onClose={() => setState({ open: false, reason: '' })}
        />
    );
};

export default PrimeModal;
